import { useEffect, useSyncExternalStore } from 'react';
import {
  ensureContentSession,
  getContentSessionServerSnapshot,
  isContentSessionReady,
  setContentSessionReady,
  subscribeContentSession,
} from '../lib/contentSession';

/** Half the ticket's 12h life, so it is always replaced well before it lapses. */
const RENEW_MS = 6 * 60 * 60 * 1000;

/**
 * KEEPS the file-content session alive. In the ordinary case it does not open
 * it — the auth bootstrap already awaited that before the app was allowed to
 * paint, because a mint started in an effect loses the race to its own images
 * (see ../lib/contentSession.ts).
 *
 * Mounted once, in the authenticated route guard, because file bytes render in
 * chat, channels, search, calls and Drive — not only on the Files page.
 */
export function useContentSession(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) {
      // Signing out, or a session held at the password-change gate, must not
      // leave the flag true: the cookie is gone or was deliberately never
      // minted, and consumers would go back to firing requests that 401.
      setContentSessionReady(false);
      return undefined;
    }

    // The one path the bootstrap cannot cover: a session that reached the app
    // through the forced-password-change gate was deliberately NOT given a
    // ticket, and nothing mints one when it is finally released. Without this,
    // completing that change left the user with working navigation and not a
    // single avatar or attachment until the renewal fired six hours later.
    if (!isContentSessionReady()) void ensureContentSession();

    const timer = window.setInterval(() => void ensureContentSession(), RENEW_MS);
    return () => window.clearInterval(timer);
  }, [enabled]);
}

/** True once the browser can authorise its own requests for file bytes. */
export function useContentSessionReady(): boolean {
  return useSyncExternalStore(
    subscribeContentSession,
    isContentSessionReady,
    getContentSessionServerSnapshot,
  );
}
